/**
 * /invoices/new — Generate a new invoice from unpaid time entries.
 *
 * InvoiceCreateInputSchema requires: clientId (required), dueInDays (optional),
 * timeEntryIds (optional — if omitted backend creates an empty draft).
 *
 * The backend generates line items from unpaid time entries. We let the user
 * pick a client, optionally set net-days, then submit. Simple, matches the schema.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Header, PageContainer } from '@/components/layout';
import { Button, Card, CardBody, Select, Input } from '@/components/ui';
import { useCreateInvoice } from '@/hooks/useInvoices';
import { useClients } from '@/hooks/useClients';
import { InvoiceCreateInputSchema } from '@aelita/shared';
import type { InvoiceCreateInput } from '@aelita/shared';

export const Route = createFileRoute('/invoices/new')({
  component: NewInvoicePage,
});

function NewInvoicePage() {
  const navigate = useNavigate();
  const createInvoice = useCreateInvoice();
  const { data: clientsData } = useClients({ status: 'active' });
  const clients = clientsData?.data ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InvoiceCreateInput>({
    resolver: zodResolver(InvoiceCreateInputSchema),
    defaultValues: { dueInDays: 30 },
  });

  async function onSubmit(data: InvoiceCreateInput) {
    try {
      const invoice = await createInvoice.mutateAsync(data);
      if (invoice?.id) {
        navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
      } else {
        navigate({ to: '/invoices' });
      }
    } catch {
      // error surfaced via mutation state
    }
  }

  return (
    <>
      <Header
        title="New Invoice"
        subtitle="Generate an invoice from unpaid time entries"
        actions={
          <Link
            to="/invoices"
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Invoices
          </Link>
        }
      />

      <PageContainer>
        <Card className="max-w-lg">
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Client */}
              <div>
                <Select
                  label="Client"
                  options={[
                    { value: '', label: 'Select a client...' },
                    ...clients.map((c: any) => ({ value: c.id, label: c.name })),
                  ]}
                  error={errors.clientId?.message}
                  {...register('clientId')}
                />
              </div>

              {/* Due in days */}
              <div>
                <Input
                  label="Net days (due in)"
                  type="number"
                  min={1}
                  max={365}
                  placeholder="30"
                  error={errors.dueInDays?.message}
                  {...register('dueInDays', { valueAsNumber: true })}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Number of days from today until payment is due. Defaults to 30 (net-30).
                </p>
              </div>

              {/* Submit error */}
              {createInvoice.isError && (
                <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg" role="alert">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Failed to generate invoice. Make sure this client has unpaid time entries, or
                    check that all fields are valid.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate({ to: '/invoices' })}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={createInvoice.isPending} className="flex-1">
                  Generate Invoice
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </PageContainer>
    </>
  );
}
